import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "react-modal";
import axios from "axios";
import { toast } from "react-toastify";
import { ChevronLeft, ChevronRight } from "lucide-react";

import Select from "../select";
import Button from "../button";
import { useLanguage } from "../../context/languageContext";
import styles from "./dayEditPopup.module.scss";

// Plain YYYY-MM-DD maths in UTC, as the week strip does, so no local timezone
// or DST shift can move a day.
const addDays = (iso, amount) => {
    const [y, m, d] = iso.split("-").map(Number);
    const date = new Date(Date.UTC(y, m - 1, d));
    date.setUTCDate(date.getUTCDate() + amount);
    return date.toISOString().slice(0, 10);
};

const longDate = (iso) => {
    const [y, m, d] = iso.split("-").map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
        weekday: "long",
        day: "numeric",
        month: "short",
        timeZone: "UTC",
    });
};

/**
 * One day of a scheduled banner — Featured Summary's book, the Picks banner's
 * person — opened by clicking that day in the week strip, with prev/next day.
 * Past days open read-only: editing them can't change what readers saw.
 *
 * - `endpoint` — the section's `/schedule` URL (GET `?from&to`, PUT `entries`).
 * - `toOption(item)` — a schedule item as a picker option `{ value, label, image }`.
 * - `toEntry(option|null)` — the PUT fields for a day; null clears it.
 * - `defaultName` — what the app shows on a day with nothing scheduled.
 * - `label` / `noun` — "Book:" / "book", "Person:" / "person".
 */
const DayEditPopup = ({
    endpoint,
    initialDate,
    options,
    selectComponents,
    toOption,
    toEntry,
    defaultName,
    label,
    noun,
    onClose,
}) => {
    const { adminInfo } = useLanguage();

    const [date, setDate] = useState(initialDate);
    const [today, setToday] = useState("");
    const [loading, setLoading] = useState(true);
    const [loadFailed, setLoadFailed] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);
    // What is saved for this day — null when nothing is scheduled; `option` is
    // null on a row whose pick has since been unpublished — and the edit.
    const [saved, setSaved] = useState(null);
    const [picked, setPicked] = useState(null);
    const [dirty, setDirty] = useState(false);
    // Whether anything was saved, so the week strip knows to refetch.
    const [changed, setChanged] = useState(false);
    // Only the latest load may fill the form: stepping through days quickly
    // could otherwise let an older day's reply land last.
    const loadRequest = useRef(0);

    const load = useCallback(async (day) => {
        const request = ++loadRequest.current;
        setLoading(true);
        setLoadFailed(false);
        // Never show — or let Clear day act on — the previous day's entry
        // under this day's date while it loads or if the load fails.
        setSaved(null);
        setPicked(null);
        setDirty(false);
        try {
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.get(endpoint, { headers, params: { from: day, to: day } });
            if (request !== loadRequest.current) return;
            if (!response.status) throw new Error(response.message);
            const entry = (response.data?.entries || []).find((e) => e.date === day);
            const item = entry?.items?.[0];
            const next = entry ? { option: item ? toOption(item) : null } : null;
            setSaved(next);
            setPicked(next?.option || null);
            if (response.data?.today) setToday(response.data.today);
        } catch (error) {
            if (request !== loadRequest.current) return;
            console.error("DayEditPopup load error::", error);
            setLoadFailed(true);
        } finally {
            if (request === loadRequest.current) setLoading(false);
        }
        // toOption is a pure mapping; leaving it out keeps an inline arrow from
        // refetching every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [adminInfo, endpoint]);

    useEffect(() => {
        if (adminInfo?.token) load(date);
    }, [adminInfo, date, load]);

    const isPast = Boolean(today) && date < today;
    const readOnly = isPast || loading || loadFailed;

    const handlePick = (option) => {
        setPicked(option);
        setDirty(true);
    };

    const write = async (fields, message) => {
        try {
            setIsSubmit(true);
            const headers = { Authorization: `Bearer ${adminInfo.token}` };
            const { data: response } = await axios.put(endpoint, { entries: [{ date, ...fields }] }, { headers });
            if (response.status) {
                toast.success(message);
                setChanged(true);
                await load(date);
            } else {
                toast.error(response.message || "Could not save this day.");
            }
        } catch (error) {
            console.error("DayEditPopup save error::", error);
            toast.error(error.response?.data?.error || "Could not save this day.");
        } finally {
            setIsSubmit(false);
        }
    };

    const handleSave = () => {
        if (!picked) {
            toast.error(`Choose a ${noun} for this day, or use Clear day to show the default.`);
            return;
        }
        write(toEntry(picked), `${longDate(date)} scheduled.`);
    };

    const handleClear = () =>
        write(toEntry(null), `${longDate(date)} cleared — it shows the default ${noun}.`);

    const close = () => onClose(changed);

    return (
        <Modal isOpen onRequestClose={close} className="modal_body_sm" overlayClassName="modal_overlay">
            <div className="box_wrapper">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title={dirty ? "Save or cancel this day first" : "Previous day"}
                        disabled={dirty || isSubmit}
                        onClick={() => setDate((d) => addDays(d, -1))}
                    >
                        <ChevronLeft size={16} />
                    </button>
                    <h4 className="fw-bold mb-0 text-center d-flex align-items-center gap-2">
                        {longDate(date)}
                        {date === today && <span className="badge bg-dark">Today</span>}
                        {isPast && <span className="badge bg-secondary">Past</span>}
                    </h4>
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title={dirty ? "Save or cancel this day first" : "Next day"}
                        disabled={dirty || isSubmit}
                        onClick={() => setDate((d) => addDays(d, 1))}
                    >
                        <ChevronRight size={16} />
                    </button>
                </div>
                <small className="text-muted d-block text-center mb-3">
                    {loadFailed
                        ? "Couldn't load this day."
                        : isPast
                            ? "This day has passed — shown as it ran."
                            : saved && !saved.option
                                ? null
                                : saved?.option
                                    ? "Scheduled for this day."
                                    : `Nothing scheduled — shows the default ${noun}.`}
                </small>

                {/* A scheduled pick that was unpublished resolves to nothing.
                    Say so rather than "nothing scheduled", which reads as
                    harmless. */}
                {saved && !saved.option && !loading && (
                    <div className={styles.warning}>
                        The {noun} scheduled for this day is no longer published.
                        {!isPast && ` Pick another, or use Clear day to show the default.`}
                    </div>
                )}

                <div className="mb-1">
                    <label className="label">{label}</label>
                    <Select
                        className="form-control-md fw-medium p-0"
                        isClearable
                        isDisabled={readOnly}
                        placeholder={defaultName ? `Default: ${defaultName}` : `Choose a ${noun}...`}
                        value={picked}
                        onChange={handlePick}
                        options={options}
                        components={selectComponents}
                    />
                </div>

                <div className="d-flex gap-2 justify-content-between mt-3">
                    <div>
                        {saved && !isPast && (
                            <button type="button" className="btn btn-outline-danger" onClick={handleClear} disabled={isSubmit || loading}>
                                Clear day
                            </button>
                        )}
                    </div>
                    <div className="d-flex gap-2">
                        <button type="button" className="btn btn-outline-secondary" onClick={close} disabled={isSubmit}>
                            {dirty ? "Cancel" : "Close"}
                        </button>
                        {!isPast && (
                            <Button onClick={handleSave} disabled={isSubmit || readOnly || !dirty} isSubmit={isSubmit} label="Saving ..." text="Save day" />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default DayEditPopup;
